import React from "react";
import { cn } from "lib/utils";

export interface OtpInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  inputClassName?: string;
  autoFocus?: boolean;
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  error = false,
  className,
  inputClassName,
  autoFocus = false,
  ...props
}: OtpInputProps) {
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

  const safeValue = React.useMemo(() => value.slice(0, length), [value, length]);
  const valueChars = React.useMemo(() => {
    const chars = safeValue.split("");
    while (chars.length < length) {
      chars.push("");
    }
    return chars;
  }, [safeValue, length]);

  const focusInput = (index: number) => {
    const input = inputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1);
    const next = [...valueChars];

    next[index] = digit ?? "";
    const nextValue = next.join("").trim();
    onChange(nextValue);

    if (digit && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !valueChars[index] && index > 0) {
      event.preventDefault();
      const next = [...valueChars];
      next[index - 1] = "";
      onChange(next.join("").trim());
      focusInput(index - 1);
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }

    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;

    onChange(pasted);
    const nextIndex = Math.min(pasted.length, length) - 1;
    if (nextIndex >= 0) {
      focusInput(nextIndex);
    }
  };

  return (
    <div className={cn("flex items-center justify-center gap-3", className)} {...props}>
      {valueChars.map((char, index) => (
        <input
          key={index}
          ref={(el) => (inputsRef.current[index] = el)}
          inputMode="numeric"
          autoComplete="one-time-code"
          type="text"
          pattern="\\d*"
          maxLength={1}
          className={cn(
            "h-12 w-11 rounded-xl border border-black bg-white text-center text-lg font-semibold tracking-[0.2em] text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
            inputClassName
          )}
          value={char}
          autoFocus={autoFocus && index === 0}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-invalid={error}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}

export default OtpInput;